/**
 * FRONT-3103: one TTS clip fetch with a stall guard and a single retry.
 *
 * The TTS backend sometimes accepts a request and then delivers nothing for a
 * minute (measured: the server's own 60 s timeout hit twice in a row, with
 * 3-4 s of audio delivered). An attempt is therefore aborted when NOTHING
 * arrives — no response headers, no body bytes — for `stallMs`. That is an
 * IDLE timer, reset on every received chunk, never a total deadline: a slow
 * phone link that keeps downloading is never cut.
 *
 * One fresh request is made after a stall, a network error or a 5xx. A 4xx is
 * never retried, and nothing is retried once the caller's own signal aborted.
 * `AbortSignal.any` is deliberately not used (missing on iOS < 17.4): the
 * caller's signal is wired to a per-attempt controller by hand.
 */
export const STALL_MS = 10_000;

interface FetchSpeechOptions {
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
  stallMs?: number;
}

class RetryableError extends Error {}

function abortError(): Error {
  const err = new Error('The operation was aborted.');
  err.name = 'AbortError';
  return err;
}

async function attemptOnce(
  body: object,
  fetchImpl: typeof fetch,
  stallMs: number,
  callerSignal: AbortSignal | undefined,
): Promise<ArrayBuffer> {
  const controller = new AbortController();
  let stalled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  // Older iOS Safari may leave a pending `reader.read()` unsettled when the
  // request is aborted, so the reader is cancelled explicitly as well.
  const abortAttempt = () => {
    controller.abort();
    reader?.cancel().catch(() => undefined);
  };
  // Rejects when THIS attempt's controller aborts. Every read is raced against
  // it: a runtime may leave a read pending after an abort, or settle it as
  // `{ done: true }` once the reader is cancelled, and the second would
  // otherwise return the partial bytes as a finished clip.
  let onControllerAbort: (() => void) | undefined;
  const aborted = new Promise<never>((_resolve, reject) => {
    onControllerAbort = () => reject(abortError());
    controller.signal.addEventListener('abort', onControllerAbort, { once: true });
  });
  aborted.catch(() => undefined);
  const arm = () => {
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(() => {
      stalled = true;
      abortAttempt();
    }, stallMs);
  };
  const onCallerAbort = abortAttempt;
  callerSignal?.addEventListener('abort', onCallerAbort, { once: true });
  try {
    if (callerSignal?.aborted) throw abortError();
    arm();
    const res = await Promise.race([
      fetchImpl('/api/voice/speak', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      }),
      aborted,
    ]);
    if (!res.ok) {
      // Drain nothing; just release the connection.
      res.body?.cancel().catch(() => undefined);
      if (res.status >= 500) throw new RetryableError(`TTS failed: ${res.status}`);
      throw new Error(`TTS failed: ${res.status}`);
    }
    arm();
    if (!res.body) {
      const buf = await Promise.race([res.arrayBuffer(), aborted]);
      if (controller.signal.aborted) throw abortError();
      return buf;
    }
    reader = res.body.getReader();
    const parts: Uint8Array[] = [];
    let total = 0;
    for (;;) {
      const { done, value } = await Promise.race([reader.read(), aborted]);
      if (done) break;
      if (value) {
        parts.push(value);
        total += value.byteLength;
      }
      arm();
    }
    // Only a stream that ended on its own is a finished clip.
    if (controller.signal.aborted) throw abortError();
    const out = new Uint8Array(total);
    let offset = 0;
    for (const part of parts) {
      out.set(part, offset);
      offset += part.byteLength;
    }
    return out.buffer;
  } catch (err) {
    if (callerSignal?.aborted) throw abortError();
    if (stalled) throw new RetryableError('TTS stalled');
    if (err instanceof RetryableError) throw err;
    // A thrown fetch/stream failure that is not an HTTP 4xx we raised
    // ourselves: treat as a network error.
    if (err instanceof Error && /^TTS failed: 4\d\d$/.test(err.message)) throw err;
    throw new RetryableError(err instanceof Error ? err.message : 'TTS network error');
  } finally {
    if (timer !== undefined) clearTimeout(timer);
    callerSignal?.removeEventListener('abort', onCallerAbort);
    if (onControllerAbort) controller.signal.removeEventListener('abort', onControllerAbort);
  }
}

export async function fetchSpeechBytes(body: object, opts: FetchSpeechOptions = {}): Promise<ArrayBuffer> {
  const fetchImpl = opts.fetchImpl ?? ((input, init) => fetch(input, init));
  const stallMs = opts.stallMs ?? STALL_MS;
  try {
    return await attemptOnce(body, fetchImpl, stallMs, opts.signal);
  } catch (err) {
    if (!(err instanceof RetryableError) || opts.signal?.aborted) throw err;
  }
  try {
    return await attemptOnce(body, fetchImpl, stallMs, opts.signal);
  } catch (err) {
    if (err instanceof RetryableError) throw new Error(err.message);
    throw err;
  }
}
